// A feedback report's activity bundle as text, unzipped in the browser and never past a cap. The
// callable refuses bundles that unzip past the same size (submitBoardFeedback's
// maxBundleExpandedBytes), but a bundle stored before it did, or written another way, could still
// be a gzip bomb: about 1,000 to 1, so a 6 MB bundle could expand to 6 GB and take the tab with it.

export const BUNDLE_TEXT_MAX_BYTES: number = 64 * 1024 * 1024;

export class BundleTooLarge extends Error {
	constructor () {
		super('The activity bundle is too large to read here');
		this.name = 'BundleTooLarge';
		// compiled to ES5, a subclass of Error loses its prototype (and instanceof) without this
		Object.setPrototypeOf(this, BundleTooLarge.prototype);
	}
}

// What readCapped needs of a stream: a reader it can read and cancel.
export interface ByteStream {
	getReader (): {
		read (): Promise<{ done: boolean, value?: Uint8Array }>
		cancel (): Promise<void>
	}
}

// The stream's bytes as text, stopping (and cancelling it) once they pass maxBytes.
export const readCapped = async (stream: ByteStream, maxBytes: number): Promise<string> => {
	const reader = stream.getReader();
	const chunks: Uint8Array[] = [];
	let total: number = 0;
	for (;;) {
		const { done, value } = await reader.read();
		if (done || !value) break;
		total += value.byteLength;
		if (total > maxBytes) {
			await reader.cancel();
			throw new BundleTooLarge();
		}
		chunks.push(value);
	}
	const all: Uint8Array = new Uint8Array(total);
	let offset: number = 0;
	chunks.forEach((chunk: Uint8Array) => {
		all.set(chunk, offset);
		offset += chunk.byteLength;
	});
	return new TextDecoder().decode(all);
};

type Decompressor = { readable: unknown, writable: unknown };
type DecompressionStreamConstructor = new (format: 'gzip') => Decompressor;

// The bundle's bytes as text: unzipped where it was gzipped, either way no more than maxBytes.
export const bundleText = async (bytes: ArrayBuffer, gzip: boolean, maxBytes: number = BUNDLE_TEXT_MAX_BYTES): Promise<string> => {
	if (!gzip) {
		if (bytes.byteLength > maxBytes) throw new BundleTooLarge();
		return new TextDecoder().decode(bytes);
	}
	const Decompression: DecompressionStreamConstructor | undefined = (window as Window & { DecompressionStream?: DecompressionStreamConstructor }).DecompressionStream;
	if (!Decompression) throw new Error('DecompressionStream is not supported');
	const unzipped: unknown = new Blob([bytes]).stream().pipeThrough(new Decompression('gzip') as Parameters<ReturnType<Blob['stream']>['pipeThrough']>[0]);
	return readCapped(unzipped as ByteStream, maxBytes);
};

// The most of one field's JSON the page parses. The board's recorder keeps 200 steps whose text
// is cut at 300 characters: about 730,000 characters at the very most, escapes and all.
export const BUNDLE_FIELD_MAX_CHARS: number = 1000 * 1000;
// How deep the steps or errors may nest. The board's are lists of flat objects: 2.
const BUNDLE_FIELD_MAX_DEPTH: number = 4;

// What the page shows of a bundle: its steps, errors and when it was packed.
export interface BundleFields {
	createdAt: unknown
	steps: unknown
	errors: unknown
}

const QUOTE: number = 0x22; // "
const BACKSLASH: number = 0x5c;
const COMMA: number = 0x2c;
const COLON: number = 0x3a;
const OPEN_OBJECT: number = 0x7b; // {
const CLOSE_OBJECT: number = 0x7d;
const OPEN_ARRAY: number = 0x5b; // [
const CLOSE_ARRAY: number = 0x5d;

const notJson = (): SyntaxError => new SyntaxError('The activity bundle is not a JSON object');

const isSpace = (c: number): boolean => c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d;

const skipSpace = (text: string, i: number): number => {
	while (i < text.length && isSpace(text.charCodeAt(i))) i++;
	return i;
};

// Where the string starting at its opening quote, i, ends: just past its closing quote.
const endOfString = (text: string, i: number): number => {
	for (let j = i + 1; j < text.length; j++) {
		const c: number = text.charCodeAt(j);
		if (c === BACKSLASH) j++;
		else if (c === QUOTE) return j + 1;
	}
	throw notJson();
};

// Where the value starting at i ends, found by its brackets and quotes alone: nothing in it is
// built, however deep or wide it is. JSON.parse checks the values the page keeps.
const endOfValue = (text: string, i: number): number => {
	const first: number = text.charCodeAt(i);
	if (first === QUOTE) return endOfString(text, i);
	if (first !== OPEN_OBJECT && first !== OPEN_ARRAY) {
		// a number, true, false or null
		let j: number = i;
		while (j < text.length) {
			const c: number = text.charCodeAt(j);
			if (c === COMMA || c === CLOSE_OBJECT || c === CLOSE_ARRAY || isSpace(c)) break;
			j++;
		}
		if (j === i) throw notJson();
		return j;
	}
	let depth: number = 0;
	for (let j = i; j < text.length; j++) {
		const c: number = text.charCodeAt(j);
		if (c === QUOTE) {
			j = endOfString(text, j) - 1;
		} else if (c === OPEN_OBJECT || c === OPEN_ARRAY) {
			depth++;
		} else if (c === CLOSE_OBJECT || c === CLOSE_ARRAY) {
			depth--;
			if (depth === 0) return j + 1;
		}
	}
	throw notJson();
};

type BundleKey = keyof BundleFields;

const BUNDLE_KEYS: BundleKey[] = ['createdAt', 'steps', 'errors'];

const hexValue = (c: number): number => {
	if (c >= 0x30 && c <= 0x39) return c - 0x30; // 0-9
	if (c >= 0x61 && c <= 0x66) return c - 0x57; // a-f
	if (c >= 0x41 && c <= 0x46) return c - 0x37; // A-F
	return -1;
};

// Whether the key at [start, end) (quotes and all) reads as `name`, its escapes decoded as they come
// and nothing built: a bundle can hold millions of keys, written any way JSON allows.
const keyIs = (text: string, start: number, end: number, name: string): boolean => {
	const stop: number = end - 1;
	let j: number = start + 1;
	for (let k = 0; k < name.length; k++) {
		if (j >= stop) return false;
		let c: number = text.charCodeAt(j);
		if (c === BACKSLASH) {
			// only \uXXXX can spell a letter; \" \\ \/ \b \f \n \r \t never do
			if (text.charCodeAt(j + 1) !== 0x75 || j + 6 > stop) return false;
			c = 0;
			for (let h = j + 2; h < j + 6; h++) {
				const digit: number = hexValue(text.charCodeAt(h));
				if (digit < 0) return false;
				c = c * 16 + digit;
			}
			j += 6;
		} else {
			j++;
		}
		if (c !== name.charCodeAt(k)) return false;
	}
	return j === stop;
};

// The field the key at [start, end) names (quotes and all), or null for any other.
const bundleKey = (text: string, start: number, end: number): BundleKey | null => {
	const length: number = end - start - 2;
	// the three are 5 to 9 letters, each written plainly or as a six-character escape
	if (length < 5 || length > 9 * 6) return null;
	return BUNDLE_KEYS.find((name: BundleKey) => keyIs(text, start, end, name)) || null;
};

// How deep the value nests, counted by its brackets outside strings.
const depthOf = (raw: string): number => {
	let depth: number = 0;
	let deepest: number = 0;
	for (let j = 0; j < raw.length; j++) {
		const c: number = raw.charCodeAt(j);
		if (c === QUOTE) {
			j = endOfString(raw, j) - 1;
		} else if (c === OPEN_OBJECT || c === OPEN_ARRAY) {
			depth++;
			if (depth > deepest) deepest = depth;
		} else if (c === CLOSE_OBJECT || c === CLOSE_ARRAY) {
			depth--;
		}
	}
	return deepest;
};

// Steps or errors, parsed only when they are as small and as flat as the board's ever are.
const parseList = (raw: string | undefined): unknown => {
	if (raw === undefined) return undefined;
	if (raw.length > BUNDLE_FIELD_MAX_CHARS) throw new BundleTooLarge();
	if (depthOf(raw) > BUNDLE_FIELD_MAX_DEPTH) throw new SyntaxError('The activity bundle\'s steps nest too deeply');
	return JSON.parse(raw);
};

// createdAt, when it is a plain number, as the board writes it.
const parseTime = (raw: string | undefined): number | undefined =>
	(raw !== undefined && raw.length <= 32 && /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?$/.test(raw) ? Number(raw) : undefined);

// The bundle's steps, errors and createdAt, parsed on their own. The rest of it (the board's
// snapshot can be tens of megabytes) is stepped over, never built: a sender could nest it a
// million levels deep, and JSON.parse would need gigabytes for it. A key given twice keeps its
// last value, as JSON.parse does. The values the page skips are only bracket-matched, so a bundle
// broken somewhere the page doesn't read still shows what it can.
export const readBundleFields = (text: string): BundleFields => {
	const raw: Record<BundleKey, string | undefined> = { createdAt: undefined, steps: undefined, errors: undefined };
	let i: number = skipSpace(text, 0);
	if (text.charCodeAt(i) !== OPEN_OBJECT) throw notJson();
	i = skipSpace(text, i + 1);
	if (text.charCodeAt(i) !== CLOSE_OBJECT) {
		for (;;) {
			if (text.charCodeAt(i) !== QUOTE) throw notJson();
			const keyEnd: number = endOfString(text, i);
			const key: BundleKey | null = bundleKey(text, i, keyEnd);
			i = skipSpace(text, keyEnd);
			if (text.charCodeAt(i) !== COLON) throw notJson();
			i = skipSpace(text, i + 1);
			const valueEnd: number = endOfValue(text, i);
			if (key) raw[key] = text.slice(i, valueEnd);
			i = skipSpace(text, valueEnd);
			const next: number = text.charCodeAt(i);
			if (next === CLOSE_OBJECT) break;
			if (next !== COMMA) throw notJson();
			i = skipSpace(text, i + 1);
		}
	}
	// nothing may follow the object
	if (skipSpace(text, i + 1) < text.length) throw notJson();
	return { createdAt: parseTime(raw.createdAt), steps: parseList(raw.steps), errors: parseList(raw.errors) };
};
